// Şub 28, 2026 — DPD Aylık/Yıllık Ortalama Çalışma Saati + Tur Adedi grafiği
// - Yıl seçimi (all-years yıl bazlı, tek yıl aylık)
// - Şoför seçimi (tek şoför drill-down)
// Composed chart: Bar (tur adedi) + Line (ort. saat)
import { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, Bar, Line,
} from 'recharts';
import { CalendarRange, Loader2, TrendingUp, Users } from 'lucide-react';

export default function DPDMonthlyWorkhoursChart({ firmId }) {
  const [year, setYear] = useState('__all__');
  const [courier, setCourier] = useState('__all__');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ firm_id: firmId });
      if (year !== '__all__') params.append('year', year);
      if (courier !== '__all__') params.append('courier', courier);
      const r = await api.get(`/dpd/monthly-workhours?${params.toString()}`);
      setData(r.data?.data || null);
    } finally {
      setLoading(false);
    }
  }, [firmId, year, courier]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const series = useMemo(() => (data?.series || []).map((s) => ({
    ...s,
    label: s.period,
  })), [data]);
  const totalTours = useMemo(() => series.reduce((a, b) => a + (b.tour_count || 0), 0), [series]);
  const avgOverall = useMemo(() => {
    const withH = series.filter((s) => s.avg_hours);
    return withH.length ? (withH.reduce((a, b) => a + b.avg_hours, 0) / withH.length).toFixed(2) : '-';
  }, [series]);

  return (
    <Card data-testid="dpd-monthly-workhours-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarRange className="h-4 w-4 text-indigo-600" />
              Aylık / Yıllık Ortalama Çalışma Saati & Tur Adedi
            </CardTitle>
            <CardDescription className="text-xs">
              {year === '__all__'
                ? 'Yıllar bazında karşılaştırma'
                : `${year} yılı — aylık dağılım`}
              {courier !== '__all__' && ` · ${courier}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="mwh-year-select">
                <SelectValue placeholder="Yıl" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tüm Yıllar</SelectItem>
                {(data?.available_years || []).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={courier} onValueChange={setCourier}>
              <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="mwh-courier-select">
                <SelectValue placeholder="Şoför" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">Tüm Şoförler</SelectItem>
                {(data?.couriers || []).map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2">
            <div className="text-[11px] text-indigo-700 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Ort. Saat/Gün</div>
            <div className="text-xl font-semibold text-indigo-700">{avgOverall}{avgOverall !== '-' && 'h'}</div>
          </div>
          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2">
            <div className="text-[11px] text-emerald-700">Toplam Tur</div>
            <div className="text-xl font-semibold text-emerald-700">{totalTours.toLocaleString()}</div>
          </div>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-2">
            <div className="text-[11px] text-amber-700 flex items-center gap-1"><Users className="h-3 w-3" /> Aktif Şoför</div>
            <div className="text-xl font-semibold text-amber-700">
              {courier === '__all__' ? (data?.couriers?.length || 0) : 1}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…
          </div>
        ) : series.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-sm text-gray-400">
            Bu seçim için veri yok.
          </div>
        ) : (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <ComposedChart data={series} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Tur', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Saat', angle: 90, position: 'insideRight', fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, k) => {
                  if (k === 'Ort. Saat') return [`${v}h`, k];
                  return [v.toLocaleString(), k];
                }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="tour_count" name="Tur Adedi" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={30} />
                <Line yAxisId="right" type="monotone" dataKey="avg_hours" name="Ort. Saat" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
